import api from './api';
import { telemetryManager } from '../telemetry/TelemetryManager';

/* =========================================================
   Interfaces
========================================================= */

export interface EngagementAnalytics {
  module_name: string;
  aggregate_interactions: number;
  aggregate_minutes_logged: number;
  unique_active_users: number;
}

export interface InactiveUser {
  username: string;
  email: string;
  module_name: string;
  last_visited_at: string;
}

export interface NeverUsedUser {
  username: string;
  email: string;
  module_name: string;
}

export interface UserEngagementSummary {
  module_name: string;
  total_click_count: number;
  total_time_spent_mins: number;
  last_visited_at: string;
}

export interface ReengagementTarget {
  username: string;
  email: string;
  module_name: string;
  last_visited_at: string;
}

/* =========================================================
   Service
========================================================= */

class TelemetryService {

  /* =========================
     Engagement Analytics
  ========================= */

  async getEngagement(): Promise<EngagementAnalytics[]> {
    if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') return [];
    try {
      const data = await api.get<any>(`/telemetry/analytics/engagement`);
      return data.data || [];
    } catch (e) {
      return [];
    }
  }

  /* =========================
     Inactive Users
  ========================= */

  async getInactiveUsers(
    module: string,
    days = 14
  ): Promise<InactiveUser[]> {
    if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') return [];
    try {
      const data = await api.get<any>(`/telemetry/analytics/inactive?module=${encodeURIComponent(module)}&days=${days}`);
      return data.data || [];
    } catch (e) {
      return [];
    }
  }

  /* =========================
     Never Used Users
  ========================= */

  async getNeverUsedUsers(
    module: string
  ): Promise<NeverUsedUser[]> {
    if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') return [];
    try {
      const data = await api.get<any>(`/telemetry/analytics/never-used?module=${encodeURIComponent(module)}`);
      return data.data || [];
    } catch (e) {
      return [];
    }
  }

  /* =========================
     Per User Summary
  ========================= */

  async getUserSummary(
    username: string
  ): Promise<UserEngagementSummary[]> {
    if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') return [];
    try {
      const data = await api.get<any>(`/telemetry/analytics/user/${encodeURIComponent(username)}`);
      return data.data || [];
    } catch (e) {
      return [];
    }
  }

  /* =========================
     Re-engagement Targets
  ========================= */

  async getReengagementTargets(
    days = 14
  ): Promise<ReengagementTarget[]> {
    if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') return [];
    try {
      const data = await api.get<any>(`/telemetry/analytics/reengagement?days=${days}`);
      return data.data || [];
    } catch (e) {
      return [];
    }
  }

  /* =========================
     Optimization Flags
  ========================= */

  async getOptimizationFlags(days = 7): Promise<any[]> {
    if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') return [];
    try {
      const data = await api.get<any>(`/telemetry/analytics/optimization-flags?days=${days}`);
      return data.data || [];
    } catch (e) {
      return [];
    }
  }

  /* =========================
     Track Event
  ========================= */

  async trackEvent(
    module_name: string,
    action_type: string,
    session_id: string
  ): Promise<boolean> {

    try {
      const focus = telemetryManager.getFocus();

      telemetryManager.track({
        moduleName: module_name,
        submoduleName: focus?.submoduleName || module_name,
        routePath: focus?.routePath || window.location.pathname,
        eventType: action_type === 'heartbeat_pulse' ? 'heartbeat' : 'action',
        actionType: action_type,
        actionLabel: action_type,
        metadata: {
          requestedSessionId: session_id,
        },
      });

      return true;

    } catch (error) {

      console.error('Telemetry tracking failed:', error);

      return false;
    }
  }

  /* =========================
     Heartbeat Pulse
  ========================= */

  async sendHeartbeat(
    module_name: string,
    session_id: string
  ): Promise<boolean> {

    return this.trackEvent(
      module_name,
      'heartbeat_pulse',
      session_id
    );
  }
}

export const telemetryService = new TelemetryService();

export default telemetryService;
